<template>
    <!--
        THE SHELL IS THREE SURFACES, NOT ONE.

        The frame (sidebar + topbar) paints `--ui-bg-chrome`, one surface across both halves.
        The page BEHIND the frame paints nothing at all, so the body's `--ui-bg-canvas` shows
        through — the canvas is a property of the theme, not of this layout, and
        `theme/theme.css` is where it is painted. That is what lets the house
        theme put the chrome and the panels on the SAME level and the page a level below them
        — white on neutral-100 in light, the panel mix on neutral-900 in dark — without this
        file knowing a colour. A theme that leaves both variables at `--ui-bg` renders the flat
        single-surface shell instead, hairlines only.

        THE CONTENT COLUMN HAS TWO RHYTHMS, one per form factor: `p-3 gap-3` below 1024px and
        `p-6 gap-6` from there up. The desktop value is unchanged; what changed is that a phone
        no longer spends 24px of a 360px-wide screen on the gutter either side of a card that
        already has padding of its own. `lg:` IS the shell's mobile breakpoint — Tailwind's
        `lg` is 1024px and so is `stores/app.ts`'s `isMobile` — so the gutter and the drawer
        flip at the same width. Keep them in sync.
    -->
    <div class="relative flex h-dvh overflow-hidden">
        <!--
            THE MOBILE BACKDROP. It paints the same black the theme gives every dialog
            (`theme/theme.config.ts` — modal, slideover, drawer), because a drawer is one of them
            even though it is our own component: one dimming, whatever is over the page.

            It is `fixed inset-0`, not absolute, so it covers the visible viewport rather than
            whatever box an ancestor happens to be — and it sits UNDER the drawer (z-40 to the
            drawer's z-50) and OVER the topbar, which is what makes tapping beside the menu the
            obvious way out of it.

            It FADES, and the fade is the same shape as the drawer's slide: 200ms out on the
            way in, 150ms in on the way out. Both ends of the transition are named as classes
            rather than left to the element's base state — `opacity-0` → `opacity-100` — so the
            browser has two values to interpolate between instead of one value and a default.
        -->
        <Transition
            enter-active-class="transition-opacity duration-200 ease-out motion-reduce:transition-none"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-150 ease-in motion-reduce:transition-none"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="isMobile && menuOpen"
                class="fixed inset-0 z-40 bg-black/50 dark:bg-black/70"
                aria-hidden="true"
                @click="closeMenu"
            />
        </Transition>

        <!--
            ONE SIDEBAR COMPONENT, TWO ROLES — and the roles are rendered SEPARATELY, because
            only one of them may animate. Below 1024px it is an overlay that slides in from the
            left; at or above it, it is the layout's own left column and must simply be there,
            with no slide when the window crosses the breakpoint. Two mutually exclusive `v-if`s
            is what buys that: the desktop column is outside the `<Transition>` entirely.

            The drawer is `fixed inset-y-0 left-0` — the visible viewport, not whatever box an
            ancestor happens to be — `h-dvh` (100vh is taller than what a mobile browser shows
            while its URL bar is out, which is exactly how the user row at its bottom went
            off-screen) and z-50, above the backdrop.

            `motion-reduce:transition-none` on both halves: a reader who has asked the system
            for less motion gets the drawer, not the slide.
        -->
        <Transition
            enter-active-class="transition-transform duration-200 ease-out motion-reduce:transition-none"
            enter-from-class="-translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transition-transform duration-150 ease-in motion-reduce:transition-none"
            leave-from-class="translate-x-0"
            leave-to-class="-translate-x-full"
        >
            <Sidebar v-if="isMobile && menuOpen" class="fixed inset-y-0 left-0 z-50 h-dvh" />
        </Transition>

        <Sidebar v-if="!isMobile" />

        <div class="relative flex flex-col flex-1 min-w-0">
            <Topbar />

            <!--
                FLEET: the content column, and the one element that scrolls — the shell is
                exactly the viewport (`h-dvh`), so the window never does; `router/index.ts`
                scrolls THIS element (`data-shell-main`) on navigation.

                Every screen but one is a stack of cards in a readable column (`max-w-7xl`,
                centred, the template's rhythm inside it). The map is the one: it is the whole
                window beside the menu and under the topbar, so a route can ask for the padding
                and the max width to be taken off (`meta.fullBleed`) rather than fighting them
                from inside — the view then fills `flex-1 min-h-0` exactly.
            -->
            <main
                v-if="fullBleed"
                data-shell-main
                class="flex flex-col flex-1 min-h-0 overflow-y-auto"
            >
                <router-view />
            </main>

            <main
                v-else
                data-shell-main
                class="flex flex-col gap-3 flex-1 overflow-y-auto p-3 lg:gap-6 lg:p-6"
            >
                <div class="mx-auto flex w-full max-w-7xl flex-col gap-3 lg:gap-6">
                    <router-view />
                </div>
            </main>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, onMounted } from 'vue'
    import { useRoute } from 'vue-router'

    import { useLayout, useLayoutShell } from '@/layouts/layout'
    import Sidebar from '@/layouts/components/Sidebar.vue'
    import Topbar  from '@/layouts/components/Topbar.vue'
    import { useAlertsStore } from '@/stores/alerts'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import { useMapSourcesStore } from '@/stores/mapSources'
    import { useRemindersStore } from '@/stores/reminders'
    import type { IMe } from '@/types/Device'

    /**
     * The shell is where the drawer's behaviour is wired — Escape, route changes, the
     * breakpoint and the body-scroll lock — and it is wired ONCE, here, because there is one
     * shell per window. `layout.ts` owns what each of those does.
     */
    const { isMobile, menuOpen, closeMenu } = useLayout()

    useLayoutShell()

    // FLEET: the map page takes the page container off (see `<main>` above).
    const route     = useRoute()
    const fullBleed = computed((): boolean => !!route.meta.fullBleed)

    const alertsStore     = useAlertsStore()
    const appStore        = useAppStore()
    const fetchStore      = useFetchStore()
    const mapSourcesStore = useMapSourcesStore()
    const remindersStore  = useRemindersStore()

    /*
     * FLEET: what the whole signed-in console needs, asked once, here.
     *
     * Who is signed in is answered by the API rather than read out of the token: the token is
     * the API's to interpret, and a 401 here is the same 401 the fetch store already turns
     * into a logout.
     *
     * Where the map comes from is one answer for the whole console, and a deployment whose API
     * does not know the question yet simply keeps the OpenStreetMap tiles it has always had
     * (stores/mapSources.ts).
     *
     * The open-alert count is started here because the menu badge is part of the shell and not
     * of any screen, and an API that predates alerting leaves it at nothing rather than drawing
     * anything (stores/alerts.ts). The opomniki's own badge is the same arrangement on a slower
     * clock (stores/reminders.ts).
     */
    onMounted(async () => {
        if (!appStore.isLoggedIn()) return

        void mapSourcesStore.load()
        alertsStore.start()
        remindersStore.start()

        const { statusCode, data } = await fetchStore.fetch('/me').get().json<IMe>()

        if (statusCode.value === 200 && data.value?.id) appStore.setUserData(data.value)
    })
</script>
